/**
 * Unit 1.4 · IMPL.A: clients table column composition.
 *
 * Bead: insurescope-11zkc.2.4.3.1. Columns are DISPLAY-ONLY; sorting is
 * driven by the page's Sort dropdown (the five backend keys), never by
 * header clicks. The actions column header is sr-only.
 */
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

import type { IClient } from "@/api";

import { ClientNameCell, CountCell, LastActivityCell } from "./clientListCells";

export interface ClientListColumnsOptions {
    /** Renders the per-row kebab (IMPL.C's ClientRowActions). */
    renderActions: (client: IClient) => ReactNode;
}

export function getClientListColumns({
    renderActions,
}: ClientListColumnsOptions): ColumnDef<IClient>[] {
    return [
        {
            id: "client",
            header: "Client",
            size: 420,
            cell: ({ row }) => <ClientNameCell client={row.original} />,
        },
        {
            id: "documents",
            header: "Docs",
            size: 90,
            cell: ({ row }) => (
                <CountCell value={row.original.document_count} />
            ),
        },
        {
            id: "scopes",
            header: "Scopes",
            size: 90,
            cell: ({ row }) => <CountCell value={row.original.scope_count} />,
        },
        {
            id: "chats",
            // The viewer's own conversations plus those shared with them;
            // matches the per-client Conversations tab.
            header: "Chats",
            size: 90,
            cell: ({ row }) => (
                <CountCell value={row.original.conversation_count} />
            ),
        },
        {
            id: "last_activity",
            header: "Last activity",
            size: 160,
            cell: ({ row }) => (
                <LastActivityCell value={row.original.last_activity} />
            ),
        },
        {
            id: "actions",
            header: () => <span className="sr-only">Actions</span>,
            size: 48,
            cell: ({ row }) => renderActions(row.original),
        },
    ];
}
